import { EscapeRing, SimulationConfig, ShapeType } from '../../types';
import { RING_VERTEX_SHADER_FINAL, RING_FRAGMENT_SHADER } from '../../shaders';
import { WebGLContext, MAX_RINGS } from './WebGLContext';
import { WebGLUtils } from './WebGLUtils';

export class RingRenderer {
  private program: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private radiusBuffer: WebGLBuffer;
  private angleBuffer: WebGLBuffer;
  private colorBuffer: WebGLBuffer;
  private alphaBuffer: WebGLBuffer;
  private gapBuffer: WebGLBuffer;
  private radiusData: Float32Array;
  private angleData: Float32Array;
  private colorData: Float32Array;
  private alphaData: Float32Array;
  private gapData: Float32Array;

  constructor(private context: WebGLContext) {
    const gl = context.gl;
    this.program = WebGLUtils.createProgram(gl, RING_VERTEX_SHADER_FINAL, RING_FRAGMENT_SHADER);
    this.radiusData = new Float32Array(MAX_RINGS);
    this.angleData = new Float32Array(MAX_RINGS);
    this.colorData = new Float32Array(MAX_RINGS * 3);
    this.alphaData = new Float32Array(MAX_RINGS);
    this.gapData = new Float32Array(MAX_RINGS);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    
    gl.bindBuffer(gl.ARRAY_BUFFER, context.geoBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.radiusBuffer = context.createAttribBuffer(1, 1, this.radiusData.byteLength);
    this.angleBuffer = context.createAttribBuffer(2, 1, this.angleData.byteLength);
    this.colorBuffer = context.createAttribBuffer(3, 3, this.colorData.byteLength);
    this.alphaBuffer = context.createAttribBuffer(4, 1, this.alphaData.byteLength);
    this.gapBuffer = context.createAttribBuffer(5, 1, this.gapData.byteLength);
    
    gl.bindVertexArray(null);
  }

  render(rings: EscapeRing[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, morph: { from: ShapeType, to: ShapeType, factor: number }, globalHue: number, zoomLevel: number = 1.0) {
      if (rings.length === 0) return;
      const gl = this.context.gl;
      gl.useProgram(this.program);
      gl.bindVertexArray(this.vao);
      
      WebGLUtils.updateCommonUniforms(gl, this.program, this.context.width, this.context.height, camera, zoomLevel);
      this.updateUniforms(config, morph);
      
      const visibleLimit = Math.min(rings.length, config.escapeMaxVisibleRings || 450);
      const count = Math.min(visibleLimit, MAX_RINGS);
      
      for (let i = 0; i < count; i++) {
          const r = rings[i];
          this.radiusData[i] = r.radius;
          this.angleData[i] = r.angle;
          this.updateColor(i, r, config, globalHue, visibleLimit);
          this.alphaData[i] = config.escapeRingAlphaBase;
          this.updateGap(i, r, config, rings.length);
      }
      
      this.uploadData(count);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
      gl.bindVertexArray(null);
  }

  renderContainer(config: SimulationConfig, camera: { x: number, y: number, scale: number }, morph: { from: ShapeType, to: ShapeType, factor: number }, radius: number, hue: number, zoomLevel: number = 1.0) {
      const gl = this.context.gl;
      gl.useProgram(this.program);
      gl.bindVertexArray(this.vao);
      
      WebGLUtils.updateCommonUniforms(gl, this.program, this.context.width, this.context.height, camera, zoomLevel);
      this.updateUniforms(config, morph, true);
      
      this.radiusData[0] = radius;
      this.angleData[0] = 0;
      WebGLUtils.parseHsl(hue, this.colorData, 0);
      this.alphaData[0] = 1.0;
      this.gapData[0] = 0;
      
      this.uploadData(1);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, 1);
      gl.bindVertexArray(null);
  }

  private updateUniforms(config: SimulationConfig, morph: { from: ShapeType, to: ShapeType, factor: number }, isContainer: boolean = false) {
    const gl = this.context.gl;
    gl.uniform2f(gl.getUniformLocation(this.program, 'u_center'), 0, 0);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_thickness'), isContainer ? (config.containerLineWidth || 4.0) : config.escapeRingThickness);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_gapSize'), (config.escapeGapSize || 30) * Math.PI / 180);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_shape'), WebGLUtils.getShapeId(morph.from));
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_nextShape'), WebGLUtils.getShapeId(morph.to));
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_morphFactor'), morph.factor);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_containerRotation'), (config.containerRotation || 0) * Math.PI / 180);
  }

  private updateColor(i: number, r: EscapeRing, config: SimulationConfig, globalHue: number, visibleLimit: number) {
      if (config.escapeColorMode === 'RAINBOW') {
          WebGLUtils.parseHsl((globalHue + i * 8 * (config.escapeRainbowSpeed || 1.0)) % 360, this.colorData, i * 3);
      } else if (config.escapeColorMode === 'UNIFORM') {
          WebGLUtils.parseColor(config.escapeUniformColor || '#3b82f6', this.colorData, i * 3);
      } else if (config.escapeColorMode === 'INDIVIDUAL') {
          const individualColors = Array.isArray(config.escapeIndividualColors) ? config.escapeIndividualColors : [];
          if (individualColors.length > 0) {
              WebGLUtils.parseColor(individualColors[i % individualColors.length], this.colorData, i * 3);
          } else {
              WebGLUtils.parseHsl(r.hue, this.colorData, i * 3);
          }
      } else if (config.escapeColorMode === 'ALTERNATING') {
          WebGLUtils.parseColor(i % 2 === 0 ? (config.escapeColorA || '#ff0000') : (config.escapeColorB || '#ffffff'), this.colorData, i * 3);
      } else if (config.escapeColorMode === 'GRADIENT') {
          const t = i / visibleLimit;
          const cA = WebGLUtils.hexToRgb(config.escapeColorA || '#ff0000');
          const cB = WebGLUtils.hexToRgb(config.escapeColorB || '#ffffff');
          this.colorData[i * 3] = (cA[0] + (cB[0] - cA[0]) * t) / 255;
          this.colorData[i * 3 + 1] = (cA[1] + (cB[1] - cA[1]) * t) / 255;
          this.colorData[i * 3 + 2] = (cA[2] + (cB[2] - cA[2]) * t) / 255;
      } else {
          WebGLUtils.parseColor(config.borderColor || '#1a1a1a', this.colorData, i * 3);
      }
  }

  private updateGap(i: number, r: EscapeRing, config: SimulationConfig, currentRingCount: number) {
      const currentShift = Math.max(0, config.escapeRingCount - currentRingCount);
      const originalIndex = currentShift + i;
      const isOpposite = originalIndex < (config.escapeOppositeGapCount || 0);
      const effectiveGapSize = isOpposite ? (config.escapeOppositeGapSize || 30) : config.escapeGapSize;
      
      let gapRad = (effectiveGapSize * Math.PI / 180);
      if (config.escapeGapAlignment === 'FIXED_SIZE') {
          const baseArcLength = (effectiveGapSize * Math.PI / 180) * config.containerRadius;
          gapRad = baseArcLength / r.radius;
      }
      this.gapData[i] = gapRad;
  }

  private uploadData(count: number) {
    const gl = this.context.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.radiusBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.radiusData.subarray(0, count));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.angleBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.angleData.subarray(0, count));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.colorData.subarray(0, count * 3));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.alphaBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.alphaData.subarray(0, count));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.gapBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.gapData.subarray(0, count));
  }
}
